import { StoreService } from '@shared/services/store.service';
import { Logger } from '@core/model/domain/logger';
import { ProcessPage } from './page';
import { NavigationFlowStrategy } from './navigationFlowStrategy';

export class LegacyNavigationFlowHandler implements NavigationFlowStrategy {
  constructor(
    private readonly storeService: StoreService,
    private readonly logger: Logger
  ) {}

  handleProcessNavigation(processPage: ProcessPage): void {
    switch (processPage) {
      case 'CONSENT':
        history.replaceState(processPage, '', '/process');
        break;
      case 'BANK_CHOICE': {
        history.pushState(processPage, '', '/process');
        break;
      }
      case 'SELECT_REGIONAL_BANK':
      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
      case 'SELECT_ACCOUNTS':
      case 'ACCOUNTS_ANALYSIS':
      case 'BANK_CONNECTION_FAILURE_RETRY':
        history.replaceState(processPage, '', '/process');
        break;
    }

    const processPageHistory = this.storeService.getVariableOrThrow('PROCESS_PAGE_HISTORY');
    processPageHistory.push(processPage);
    this.storeService.setVariable('PROCESS_PAGE_HISTORY', processPageHistory);
  }

  handlePopstate(): void {
    const shouldIgnoreNextPopstate = this.storeService.getVariableOrThrow('SHOULD_IGNORE_NEXT_POPSTATE');

    if (shouldIgnoreNextPopstate) {
      this.storeService.setVariable('SHOULD_IGNORE_NEXT_POPSTATE', false);
      return;
    }

    const errorRoute = window.location.href.includes('/error');
    if (!errorRoute) {
      this.handlePopStateOnProcessRoute();
      return;
    }

    this.handleErrorRouteBrowserNavigationWhenPopstate();
  }

  handleGoBackViaInAppButton() {
    this.storeService.setVariable('IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON', true);
    history.back();
  }

  /* 
    To disable browser forward button we need to apply programmaticaly a history.forward each 
    time we go back in history stack. This will put the history stack index to the last 
    element (history.length-1), and thus, forward button won't be pushable on browser.

    Steps :
      1. In the popstate event callback, history.state returns the previous state in the stack.
         If you're at LOGIN (history.length-1) and click back button, popstate is triggered, 
         and history.state returns history.length-2 = BANK_CHOICE

      2. history.forward allows us to go to history.length-1 again to block browser 
         forward button. But, since we forwarded, the state will be LOGIN instead of 
         BANK_CHOICE, we need to replace it
      
      3. Thus, we need to apply replaceState to be consistent. But, doing it right after 
         history.forward will replace the state of history.length-2. And that's because 
         history.forward is asynchronous, it's handled by the task queue. By replacing
         state in a setTimeout, replaceState will also be in the task queue, after
         history.forward. Thus, it'll replace history.length-1, and LOGIN will be
         BANK_CHOICE
         https://www.w3.org/TR/2011/WD-html5-20110113/history.html#traverse-the-history-by-a-delta
         
    Also, by calling history.forward in popstate, we'll trigger popstate again once the current popstate
    callback is fin, but we don't wan't to process this one. To avoid it, we use SHOULD_IGNORE_NEXT_POPSTATE.
  */
  private handlePopStateOnProcessRoute(): void {
    const isBackNavigationPerformedViaOpenBankingButton = this.storeService.getVariableOrThrow(
      'IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON'
    );

    if (!isBackNavigationPerformedViaOpenBankingButton) {
      this.logger.logEvent('BROWSER_BACK_BUTTON_PUSHED');
    }
    this.storeService.setVariable('IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON', false);

    this.handlePopStateOnProcessHistory();
    this.handleProcessPageToDisplayWhenPopstate();
  }

  private handleErrorRouteBrowserNavigationWhenPopstate() {
    this.logger.logEvent('BROWSER_BACK_BUTTON_PUSHED');
  }

  private handlePopStateOnProcessHistory(): void {
    const processPageHistory = this.storeService.getVariableOrThrow('PROCESS_PAGE_HISTORY');
    const previousPage = processPageHistory[processPageHistory.length - 1];

    switch (previousPage) {
      case 'BANK_CHOICE':
        // Will trigger before unload
        this.storeService.setVariable('PROCESS_PAGE_HISTORY', ['CONSENT', 'BANK_CHOICE']);
        history.back();
        return;
      case 'SELECT_REGIONAL_BANK':
      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
      case 'SELECT_ACCOUNTS':
      case 'NONE_ACCOUNTS':
      case 'BANK_CONNECTION_FAILURE_RETRY':
        history.forward();
        setTimeout(() => {
          history.replaceState('BANK_CHOICE', '', '/process');
        }, 0);
        this.storeService.setVariable('PROCESS_PAGE_HISTORY', ['CONSENT', 'BANK_CHOICE']);
        break;

      case 'ACCOUNTS_ANALYSIS':
        {
          const selectedAccountsAnalysisState =
            this.storeService.getVariableOrThrow('ACCOUNT_ANALYSIS_STATE');

          const shouldGoBackToBankChoice =
            selectedAccountsAnalysisState !== 'SUCCESS' && selectedAccountsAnalysisState !== 'FAILED';

          if (shouldGoBackToBankChoice) {
            history.forward();

            setTimeout(() => {
              history.replaceState('BANK_CHOICE', '', '/process');
            }, 0);
            this.storeService.setVariable('PROCESS_PAGE_HISTORY', ['CONSENT', 'BANK_CHOICE']);
            break;
          }

          history.forward();
          setTimeout(() => {
            history.replaceState(null, '', '/process');
          }, 0);
        }
        break;
    }

    this.storeService.setVariable('SHOULD_IGNORE_NEXT_POPSTATE', true);
  }

  private handleProcessPageToDisplayWhenPopstate(): void {
    const currentProcessPage = this.storeService.getVariableOrThrow('PROCESS_PAGE');

    switch (currentProcessPage) {
      case 'SELECT_REGIONAL_BANK':
      case 'WAIT_ON_EXTERNAL_BANK_LOGIN':
      case 'SELECT_ACCOUNTS':
      case 'NONE_ACCOUNTS':
      case 'BANK_CONNECTION_FAILURE_RETRY':
        this.storeService.setVariable('PROCESS_PAGE', 'BANK_CHOICE');

        break;
      case 'ACCOUNTS_ANALYSIS': {
        const selectedAccountsAnalysisState = this.storeService.getVariableOrThrow('ACCOUNT_ANALYSIS_STATE');

        const shouldGoBackToBankChoice =
          selectedAccountsAnalysisState !== 'SUCCESS' && selectedAccountsAnalysisState !== 'FAILED';

        if (shouldGoBackToBankChoice) {
          this.storeService.setVariable('PROCESS_PAGE', 'BANK_CHOICE');
          break;
        }
      }
    }

    setTimeout(() => {
      window.scrollTo({ top: 0 });
    }, 0);
  }
}
